import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { Box, Container, Typography, Paper, CircularProgress, Grid, Alert, Button, Stack, TextField, ThemeProvider, createTheme, Tabs, Tab } from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import WarningIcon from "@mui/icons-material/Warning";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TimerIcon from "@mui/icons-material/Timer";
import BuildIcon from "@mui/icons-material/Build";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StarIcon from "@mui/icons-material/Star";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import moment from "moment";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import useDashboard from "../../hooks/useDashboard";
import api from "../../services/api";
import CardCounter from "../../components/Dashboard/CardCounter";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import SobrecargaChart from "./SobrecargaChart";
import { ChatsUser } from "./ChartsUser";
import ChartsDate from "./ChartsDate";
import RankingPodium from "./RankingPodium";
import AttendanceReportTab from "./AttendanceReportTab";
import { AuthContext } from "../../context/Auth/AuthContext";
import { hasPermission } from "../../helpers/hasPermission";

const DASHBOARD_TABS = [
  { key: "overview", label: "Visão Geral", permission: "dashboard:tab:overview" },
  { key: "quality", label: "Qualidade & Carga", permission: "dashboard:tab:quality" },
  { key: "detailed", label: "Relatório Detalhado", permission: "dashboard:tab:detailed" }
];

const formatTime = (minutes) => moment().startOf("day").add(minutes || 0, "minutes").format("HH[h] mm[m]");

const VOLUME_COLOR = "#2563eb";
const TIME_COLOR = "#0891b2";
const SUCCESS_COLOR = "#16a34a";
const NEUTRAL_COLOR = "#64748b";
const DANGER_COLOR = "#dc2626";

const SectionTitle = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ display: "block", fontWeight: 700, letterSpacing: 1, color: "text.secondary", mb: 1, mt: 3 }}
  >
    {children}
  </Typography>
);

const Dashboard = () => {
  const themeV4 = useThemeV4();

  const v5Theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeV4.palette.type === "dark" ? "dark" : "light",
          primary: { main: themeV4.palette.primary.main },
          background: {
            default: themeV4.palette.background?.default,
            paper: themeV4.palette.type === "dark" ? "#26292d" : "#ffffff",
          },
        },
        shape: { borderRadius: 10 },
      }),
    [themeV4.palette.type, themeV4.palette.primary.main, themeV4.palette.background]
  );

  const { user } = useContext(AuthContext);
  const visibleTabs = useMemo(
    () => DASHBOARD_TABS.filter(t => hasPermission(user, t.permission)),
    [user]
  );
  const [tab, setTab] = useState(0);
  const [counters, setCounters] = useState({});
  const [ratingEnabled, setRatingEnabled] = useState(true);
  const [attendants, setAttendants] = useState([]);
  const [dateStartTicket, setDateStartTicket] = useState(moment().format("YYYY-MM-DD"));
  const [dateEndTicket, setDateEndTicket] = useState(moment().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overloaded, setOverloaded] = useState([]);
  const { find } = useDashboard();

  const fetchData = useCallback(async () => {
    if (loading) return;
    if (!moment(dateStartTicket, "YYYY-MM-DD", true).isValid() || !moment(dateEndTicket, "YYYY-MM-DD", true).isValid()) {
      // data ainda sendo digitada/editada - espera ficar valida antes de consultar
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = {
        date_from: moment(dateStartTicket).format("YYYY-MM-DD"),
        date_to: moment(dateEndTicket).format("YYYY-MM-DD")
      };
      const data = await find(params);
      if (data) {
        setCounters(data.counters || {});
        setRatingEnabled(data.ratingEnabled !== false);
        setAttendants((data.attendants || []).filter(a => (a.tickets || 0) > 0));
      }
    } catch (err) {
      setError("Erro de permissão (403/401). Verifique o status da empresa ou faça novo login.");
    } finally {
      setLoading(false);
    }
  }, [dateStartTicket, dateEndTicket, find]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchOverload = useCallback(async () => {
    if (!moment(dateStartTicket, "YYYY-MM-DD", true).isValid() || !moment(dateEndTicket, "YYYY-MM-DD", true).isValid()) {
      return;
    }
    try {
      const { data } = await api.get("/dashboard/tecnicos-sobrecarga", {
        params: {
          dataInicial: moment(dateStartTicket).format("YYYY-MM-DD"),
          dataFinal: moment(dateEndTicket).format("YYYY-MM-DD"),
        }
      });
      setOverloaded((data?.tecnicos || []).filter(t => t.status === "Sobrecarregado"));
    } catch (err) {
      setOverloaded([]);
    }
  }, [dateStartTicket, dateEndTicket]);

  useEffect(() => {
    fetchOverload();
  }, [fetchOverload]);

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    const resumo = [{
      Periodo: `${dateStartTicket} a ${dateEndTicket}`,
      EmAtendimento: counters.supportHappening || 0,
      Aguardando: counters.supportPending || 0,
      Finalizados: counters.supportFinished || 0,
      TempoMedioEspera: formatTime(counters.avgWaitTime),
      TempoMedioAtendimento: formatTime(counters.avgSupportTime),
      TempoMedioTotal: formatTime(counters.avgTotalTime),
      NpsScore: counters.npsScore || 0,
    }];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumo), "Resumo");

    const atendentesRows = attendants.map(a => ({
      Atendente: a.name,
      Tickets: a.tickets,
      TempoMedioEspera: formatTime(a.avgWaitTime),
      TempoMedioAtendimento: formatTime(a.avgSupportTime),
      TempoMedioTotal: formatTime(a.avgTotalTime),
      Nota: a.rating,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(atendentesRows), "Atendentes");

    if (overloaded.length > 0) {
      const sobrecargaRows = overloaded.map(t => ({
        Tecnico: t.name,
        TicketsAtivos: t.tickets_ativos,
        LimiteMaximo: t.limite_maximo_simultaneo,
        VezesSobrecarregado: t.vezes_sobrecarregado || 0,
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sobrecargaRows), "Sobrecarga");
    }

    XLSX.writeFile(wb, `dashboard-${dateStartTicket}-a-${dateEndTicket}.xlsx`);
  };

  const handleExportPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Dashboard de Gestao", 14, 16);
    doc.setFontSize(10);
    doc.text(`Periodo: ${dateStartTicket} a ${dateEndTicket}`, 14, 23);

    autoTable(doc, {
      startY: 28,
      head: [["Em Atendimento", "Aguardando", "Finalizados", "T.M. Espera", "T.M. Atendimento", "T.M. Total", "NPS"]],
      body: [[
        counters.supportHappening || 0,
        counters.supportPending || 0,
        counters.supportFinished || 0,
        formatTime(counters.avgWaitTime),
        formatTime(counters.avgSupportTime),
        formatTime(counters.avgTotalTime),
        counters.npsScore || 0,
      ]],
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [["Atendente", "Tickets", "T.M. Espera", "T.M. Atendimento", "T.M. Total", "Nota"]],
      body: attendants.map(a => [
        a.name,
        a.tickets,
        formatTime(a.avgWaitTime),
        formatTime(a.avgSupportTime),
        formatTime(a.avgTotalTime),
        a.rating,
      ]),
    });

    if (overloaded.length > 0) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [["Tecnico Sobrecarregado", "Tickets Ativos", "Limite Maximo", "Vezes sobrecarregado"]],
        body: overloaded.map(t => [t.name, t.tickets_ativos, t.limite_maximo_simultaneo, t.vezes_sobrecarregado || 0]),
      });
    }

    doc.save(`dashboard-${dateStartTicket}-a-${dateEndTicket}.pdf`);
  };

  return (
    <ThemeProvider theme={v5Theme}>
      <Box sx={{ p: 2, bgcolor: "background.default", minHeight: "100%" }}>
        <Container maxWidth="xl">
          <Grid container justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Grid item>
              <Typography variant="h5" fontWeight="bold">Dashboard de Gestão</Typography>
              <Typography variant="body2" color="text.secondary">
                Acompanhe o desempenho do time em tempo real
              </Typography>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleExportExcel}>
                  Excel
                </Button>
                <Button variant="outlined" size="small" color="error" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}>
                  PDF
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {overloaded.length > 0 && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
              {overloaded.length === 1
                ? `${overloaded[0].name} está com volume de atendimentos simultâneos acima do normal. Considere redistribuir atendimentos.`
                : `${overloaded.length} técnicos estão com volume de atendimentos simultâneos acima do normal (${overloaded.map(t => t.name).join(", ")}). Considere redistribuir atendimentos.`}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2.5 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Data inicial"
                  InputLabelProps={{ shrink: true }}
                  value={dateStartTicket}
                  onChange={(e) => setDateStartTicket(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Data final"
                  InputLabelProps={{ shrink: true }}
                  value={dateEndTicket}
                  onChange={(e) => setDateEndTicket(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>

          {visibleTabs.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2.5 }}>
              <Typography color="text.secondary">
                Nenhuma aba liberada para o seu usuário — peça a um administrador pra liberar em Usuários → Permissões.
              </Typography>
            </Paper>
          ) : (
            <>
              <Tabs
                value={Math.min(tab, visibleTabs.length - 1)}
                onChange={(e, v) => setTab(v)}
                textColor="primary"
                indicatorColor="primary"
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
              >
                {visibleTabs.map(t => (
                  <Tab key={t.key} label={t.label} />
                ))}
              </Tabs>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}><CircularProgress /></Box>
              ) : (
                <>
              {visibleTabs[Math.min(tab, visibleTabs.length - 1)]?.key === "overview" && (
                <>
                  <SectionTitle>Resumo do período</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <CardCounter icon={<FormatListNumberedIcon fontSize="inherit" />} color={VOLUME_COLOR} title="Total de Atendimentos" value={(counters.supportHappening || 0) + (counters.supportPending || 0) + (counters.supportFinished || 0)} loading={loading} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CardCounter icon={<SupportAgentIcon fontSize="inherit" />} color={VOLUME_COLOR} title="Em Atendimento" value={counters.supportHappening || 0} loading={loading} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CardCounter icon={<HourglassEmptyIcon fontSize="inherit" />} color={VOLUME_COLOR} title="Aguardando" value={counters.supportPending || 0} loading={loading} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CardCounter icon={<CheckCircleIcon fontSize="inherit" />} color={VOLUME_COLOR} title="Finalizados" value={counters.supportFinished || 0} loading={loading} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CardCounter icon={<TimerIcon fontSize="inherit" />} color={TIME_COLOR} title="Tempo de Espera" value={formatTime(counters.avgWaitTime)} loading={loading} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CardCounter icon={<BuildIcon fontSize="inherit" />} color={TIME_COLOR} title="Tempo de Atendimento" value={formatTime(counters.avgSupportTime)} loading={loading} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CardCounter icon={<ScheduleIcon fontSize="inherit" />} color={TIME_COLOR} title="Tempo Total" value={formatTime(counters.avgTotalTime)} loading={loading} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CardCounter icon={<StarIcon fontSize="inherit" />} color={SUCCESS_COLOR} title="NPS Score" value={counters.npsScore || 0} loading={loading} />
                    </Grid>
                  </Grid>

                  <SectionTitle>Ranking e desempenho dos atendentes</SectionTitle>
                  <RankingPodium attendants={attendants} />
                  <TableAttendantsStatus attendants={attendants} loading={loading} />

                  <SectionTitle>Atendimentos por dia</SectionTitle>
                  <ChartsDate dateStart={dateStartTicket} dateEnd={dateEndTicket} />
                </>
              )}

              {visibleTabs[Math.min(tab, visibleTabs.length - 1)]?.key === "quality" && (
                <>
                  <SectionTitle>Sobrecarga de atendimentos</SectionTitle>
                  <SobrecargaChart dateStart={dateStartTicket} dateEnd={dateEndTicket} />

                  <SectionTitle>Avaliações (NPS)</SectionTitle>
                  {!ratingEnabled ? (
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2.5 }}>
                      <Typography color="text.secondary">
                        A avaliação de atendimento (NPS) está desativada nas configurações da empresa - por isso não há cards aqui. Os tickets fechados nesse período não contam como "sem avaliação", eles simplesmente nunca pedem avaliação nenhuma.
                      </Typography>
                    </Paper>
                  ) : (
                    <>
                      <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <CardCounter icon={<SentimentSatisfiedAltIcon fontSize="inherit" />} color={SUCCESS_COLOR} title="Promotores" value={`${counters.npsPromotersPerc || 0}%`} loading={loading} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <CardCounter icon={<SentimentNeutralIcon fontSize="inherit" />} color={NEUTRAL_COLOR} title="Neutros" value={`${counters.npsPassivePerc || 0}%`} loading={loading} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <CardCounter icon={<SentimentDissatisfiedIcon fontSize="inherit" />} color={DANGER_COLOR} title="Detratores" value={`${counters.npsDetractorsPerc || 0}%`} loading={loading} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <CardCounter icon={<HowToRegIcon fontSize="inherit" />} color={VOLUME_COLOR} title="Avaliações Recebidas" value={counters.withRating || 0} loading={loading} />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <CardCounter icon={<PendingActionsIcon fontSize="inherit" />} color={NEUTRAL_COLOR} title="Sem Avaliação" value={counters.withoutRating || 0} loading={loading} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <CardCounter icon={<HourglassEmptyIcon fontSize="inherit" />} color={TIME_COLOR} title="Aguardando Avaliação" value={counters.waitRating || 0} loading={loading} />
                        </Grid>
                      </Grid>
                    </>
                  )}
                </>
              )}

              {visibleTabs[Math.min(tab, visibleTabs.length - 1)]?.key === "detailed" && (
                <>
                  <AttendanceReportTab dateStart={dateStartTicket} dateEnd={dateEndTicket} />

                  <SectionTitle>Atendimentos por atendente</SectionTitle>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2.5 }}>
                    <ChatsUser dateStart={dateStartTicket} dateEnd={dateEndTicket} />
                  </Paper>
                </>
              )}
                </>
              )}
            </>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};
export default Dashboard;
